import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Icon, Loader, Modal, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "semantic-ui-react";

import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { createFormHelper } from "lib/Form";
import { CUSTOMER_TYPES } from "models";
import { customerTypeMap } from "modules/Appointments/components";
import "modules/Appointments/components/NewAppointment/components/CustomerSearchFormSection/components/CustomerExternalDataModal/CustomerExternalDataModal.scss";
import { useLessorROBDataQuery } from "modules/Appointments/components/NewAppointment/hooks";

import type { Customer } from "models";
import type { AppointmentSection, CustomerSection } from "modules/Appointments/components/NewAppointment";

type CarExternalDataModalProps = {
  open: boolean;
  activeCustomerTab: CUSTOMER_TYPES;
  customer?: Customer | null;
  onClose: () => void;
};

type FindMatchingCustomerKeyType = {
  formCustomerDriver: CustomerSection | null;
  formCustomerOwner: CustomerSection | null;
  formCustomerContract: CustomerSection | null;
  customer?: Customer | null;
};

const findMatchingCustomerKey = ({ formCustomerContract, formCustomerDriver, formCustomerOwner, customer }: FindMatchingCustomerKeyType) => {
  if (!customer?.customer_type) return null;

  if (formCustomerDriver?.customer_type === customer.customer_type) return "customer_driver";
  if (formCustomerOwner?.customer_type === customer.customer_type) return "customer_owner";
  if (formCustomerContract?.customer_type === customer.customer_type) return "customer_contract";
  return null;
};

const FormHelper = createFormHelper<AppointmentSection>();

export const CustomerExternalDataModal = ({ open, customer, activeCustomerTab, onClose }: CarExternalDataModalProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { setValue, watch } = FormHelper.useFormContext();
  const [regNr, formCustomerContract, formCustomerDriver, formCustomerOwner] = watch(["car.reg_nr", "customer_contract", "customer_driver", "customer_owner"]);

  const { data: robData, refetch: refetchRobData, isFetching: isFetchingRobData } = useLessorROBDataQuery(regNr, selectedLocation?.id);
  const isRobNetEnabled = selectedLocation?.is_robnet_enabled;

  const [selectedFields, setSelectedFields] = useState({ company: false, street: false, houseNr: false, postcode: false, city: false, phone: false });
  const [lastCarReg, setLastCarReg] = useState<string | undefined>(regNr);
  const [hasSaved, setHasSaved] = useState(false);

  const hasSelectedFields = Object.values(selectedFields).some(value => value === true);
  const isSelectAll = selectedFields.company && selectedFields.street && selectedFields.houseNr && selectedFields.postcode && selectedFields.city && selectedFields.phone;
  const toggleField = (key: keyof typeof selectedFields) => setSelectedFields(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleSelectAll = () =>
    setSelectedFields({ company: !isSelectAll, street: !isSelectAll, houseNr: !isSelectAll, postcode: !isSelectAll, city: !isSelectAll, phone: !isSelectAll });

  useEffect(() => {
    if (open && isRobNetEnabled) refetchRobData();
  }, [open, isRobNetEnabled]);

  useEffect(() => {
    if (regNr !== lastCarReg) {
      setSelectedFields({ company: false, street: false, houseNr: false, postcode: false, city: false, phone: false });
      setLastCarReg(regNr);
      setHasSaved(false);
    }
  }, [regNr, lastCarReg, hasSaved]);

  const handleCancel = () => {
    if (!hasSaved) setSelectedFields({ company: false, street: false, houseNr: false, postcode: false, city: false, phone: false });
    onClose();
  };

  const getTargetCustomer = () => {
    if (customer?.customer_type === CUSTOMER_TYPES.NotSet) return customerTypeMap[activeCustomerTab];
    return findMatchingCustomerKey({ formCustomerContract, formCustomerDriver, formCustomerOwner, customer });
  };

  const handleSave = () => {
    const targetCustomer = getTargetCustomer();
    if (!targetCustomer || targetCustomer === "not_set") return;

    if (selectedFields.company && robData?.name) setValue(`${targetCustomer}.company`, robData.name);
    if (selectedFields.street && robData?.address.street) setValue(`${targetCustomer}.street`, robData.address.street);
    if (selectedFields.houseNr && robData?.address.streetNumber) setValue(`${targetCustomer}.house_nr`, robData.address.streetNumber);
    if (selectedFields.postcode && robData?.address.postalCode) setValue(`${targetCustomer}.postcode`, robData.address.postalCode);
    if (selectedFields.city && robData?.address.city) setValue(`${targetCustomer}.place`, robData.address.city);
    if (selectedFields.phone && robData?.phone.general) setValue(`${targetCustomer}.tel_business_nr`, robData.phone.general);

    setHasSaved(true);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="small" className="CustomerExternalDataModal">
      <Modal.Header>{t("v8_customer_external_import", "Customer - External Import")}</Modal.Header>
      <Modal.Content className="modal-content">
        {isFetchingRobData && <Loader active inline="centered" inverted className="Loader" />}
        {!isFetchingRobData && !robData && <p className="no-data">{t("v8_no_data", "No data")}</p>}
        {!isFetchingRobData && robData && (
          <Table basic="very" fixed singleLine>
            <TableHeader>
              <TableRow textAlign="left">
                <TableHeaderCell>{t("v8_field", "Field")}</TableHeaderCell>
                <TableHeaderCell>{t("v8_current_value", "Current value")}</TableHeaderCell>
                <TableHeaderCell className="checkbox-wrapper">
                  <div className="select-all-wrapper">
                    <Checkbox checked={isSelectAll} disabled={!robData} onChange={toggleSelectAll} />
                  </div>
                  <span className="header-tag">ROB</span>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              <TableRow>
                <TableCell>{t("v8_company_name", "Company name")}</TableCell>
                <TableCell>{customer?.company}</TableCell>
                {robData.name && (
                  <TableCell className="checkbox-wrapper">
                    <Checkbox checked={selectedFields.company} disabled={!robData} onChange={() => toggleField("company")} />
                    <span>{robData.name}</span>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>{t("v8_street", "Street")}</TableCell>
                <TableCell>{customer?.street}</TableCell>
                {robData.address.street && (
                  <TableCell className="checkbox-wrapper">
                    <Checkbox checked={selectedFields.street} disabled={!robData} onChange={() => toggleField("street")} />
                    <span>{robData.address.street}</span>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>{t("v8_house_number", "House Number")}</TableCell>
                <TableCell>{customer?.house_nr}</TableCell>
                {robData.address.streetNumber && (
                  <TableCell className="checkbox-wrapper">
                    <Checkbox checked={selectedFields.houseNr} disabled={!robData} onChange={() => toggleField("houseNr")} />
                    <span>{robData.address.streetNumber}</span>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>{t("v8_postal_code", "Postal Code")}</TableCell>
                <TableCell>{customer?.postcode}</TableCell>
                {robData.address.postalCode && (
                  <TableCell className="checkbox-wrapper">
                    <Checkbox checked={selectedFields.postcode} disabled={!robData} onChange={() => toggleField("postcode")} />
                    <span>{robData.address.postalCode}</span>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>{t("v8_city", "City")}</TableCell>
                <TableCell>{customer?.place}</TableCell>
                {robData.address.city && (
                  <TableCell className="checkbox-wrapper">
                    <Checkbox checked={selectedFields.city} disabled={!robData} onChange={() => toggleField("city")} />
                    <span>{robData.address.city}</span>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>{t("v8_phone_number", "Phone Number")}</TableCell>
                <TableCell>{customer?.tel_business_nr}</TableCell>
                {robData.phone.general && (
                  <TableCell className="checkbox-wrapper">
                    <Checkbox checked={selectedFields.phone} disabled={!robData} onChange={() => toggleField("phone")} />
                    <span>{robData.phone.general}</span>
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Modal.Content>
      <Modal.Actions className="modal-actions">
        <Button className="light" onClick={handleCancel} type="reset">
          {t("v8_cancel", "Cancel")}
          <Icon className="xmark" />
        </Button>
        <Button color="green" onClick={handleSave} disabled={!robData || !hasSelectedFields}>
          {t("v8_update", "Update")}
          <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
